import "./AiAssistantModal.scss";
import React, {KeyboardEvent, useEffect, useRef, useState} from "react";
import {Button, Input, Modal} from "rsuite";
import {useNavigate} from "react-router-dom";
import {
  AssistantArticle,
  getAvailableAssistantArticles,
  searchAssistantKnowledge
} from "../../../services/AssistantKnowledge.service";
import {AiAssistantIcon, FiSendIcon} from "../../../models/Icons.model";
import {
  saveAssistantFeedback,
  saveUnansweredQuestion
} from "../../../services/AssistantFeedback.service";
import {
  askDeepSeekAssistant,
  DeepSeekConversationMessage,
  isDeepSeekConfigured
} from "../../../services/DeepSeekAssistant.service";

type AiAssistantModalProps = {
  open: boolean;
  onClose: () => void;
  deviceId: string | null;
  currentRoute: string;
  isClockStation?: boolean;
  hasMelody?: boolean;
};

type ChatMessage = {
  id: number;
  role: "assistant" | "user";
  text?: string;
  question?: string;
  article?: AssistantArticle;
  suggestions?: AssistantArticle[];
  feedback?: boolean;
  generatedByAi?: boolean;
  loading?: boolean;
};

const quickQuestions = [
  "Как настроить вторичные часы?",
  "Как включить синхронизацию по NTP?",
  "Как настроить Wi-Fi?"
];

const initialMessage: ChatMessage = {
  id: 1,
  role: "assistant",
  text: "Здравствуйте! Я помогу найти нужный раздел и объясню настройку. Что вы хотите сделать?"
};

const MAX_HISTORY_MESSAGES = 10;

const getMessageContent = (message: ChatMessage) => {
  if (message.generatedByAi && message.text) return message.text;

  const parts = [message.text];
  if (message.article) {
    parts.push(message.article.title, ...message.article.content);
  }
  return parts.filter(Boolean).join("\n");
};

const getConversationHistory = (messages: ChatMessage[]): DeepSeekConversationMessage[] => messages
  .filter((message) => !message.loading)
  .map((message) => ({
    role: message.role,
    content: getMessageContent(message)
  }))
  .filter((message) => message.content)
  .slice(-MAX_HISTORY_MESSAGES);

const AiAssistantModal = ({
  open,
  onClose,
  deviceId,
  currentRoute,
  isClockStation,
  hasMelody
}: AiAssistantModalProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [isAnswering, setIsAnswering] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const nextMessageId = useRef(2);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => messagesEndRef.current?.scrollIntoView({behavior: "smooth"}), 0);
    }
  }, [messages, open]);

  const addAnswer = async (question: string) => {
    const cleanQuestion = question.trim();
    if (!cleanQuestion || isAnswering) return;

    const searchContext = {
      currentRoute,
      isClockStation,
      hasMelody
    };

    const results = searchAssistantKnowledge(cleanQuestion, searchContext);
    const availableArticles = getAvailableAssistantArticles(searchContext);
    const conversationHistory = getConversationHistory(messages);

    const userMessage: ChatMessage = {
      id: nextMessageId.current++,
      role: "user",
      text: cleanQuestion
    };

    const answerId = nextMessageId.current++;
    const localAnswer: ChatMessage = results.length > 0
      ? {
          id: answerId,
          role: "assistant",
          question: cleanQuestion,
          article: results[0].article,
          suggestions: results.slice(1).map((result) => result.article)
        }
      : {
          id: answerId,
          role: "assistant",
          text: "Пока не нашёл точную инструкцию. Попробуйте назвать раздел или действие, например: «настроить вторичные часы», «включить NTP» или «подключить Wi‑Fi»."
        };

    setMessages((current) => [
      ...current,
      userMessage,
      isDeepSeekConfigured
        ? {id: answerId, role: "assistant", text: "Формирую ответ…", loading: true}
        : localAnswer
    ]);
    setQuery("");

    if (!isDeepSeekConfigured) {
      if (results.length === 0) saveUnansweredQuestion(cleanQuestion, currentRoute);
      return;
    }

    setIsAnswering(true);
    try {
      const deepSeekAnswer = await askDeepSeekAssistant(
        cleanQuestion,
        availableArticles,
        searchContext,
        conversationHistory
      );
      const matchedArticle = availableArticles.find((article) => article.id === deepSeekAnswer.articleId)
        || results[0]?.article;

      if (!matchedArticle) saveUnansweredQuestion(cleanQuestion, currentRoute);

      setMessages((current) => current.map((message) => message.id === answerId
        ? {
            id: answerId,
            role: "assistant",
            question: cleanQuestion,
            text: deepSeekAnswer.answer,
            article: matchedArticle,
            generatedByAi: true
          }
        : message));
    } catch (error) {
      console.error("DeepSeek assistant error:", error);
      if (results.length === 0) saveUnansweredQuestion(cleanQuestion, currentRoute);
      setMessages((current) => current.map((message) => message.id === answerId
        ? {
            ...localAnswer,
            text: localAnswer.article
              ? "DeepSeek сейчас недоступен. Показываю найденную инструкцию из локальной базы."
              : localAnswer.text
          }
        : message));
    } finally {
      setIsAnswering(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      addAnswer(query);
    }
  };

  const resolveRoute = (template: string) => deviceId
    ? template.replace(":deviceId", deviceId)
    : null;

  const handleOpenArticle = (article: AssistantArticle) => {
    const route = resolveRoute(article.route.template);
    if (!route) return;
    onClose();
    navigate(route);
  };

  const handleFeedback = (message: ChatMessage, helpful: boolean) => {
    if (!message.article || !message.question || message.feedback !== undefined) return;

    saveAssistantFeedback(message.question, message.article.id, helpful, currentRoute);
    setMessages((current) => current.map((item) => (
      item.id === message.id ? {...item, feedback: helpful} : item
    )));
  };

  const handleNewConversation = () => {
    if (isAnswering) return;
    setMessages([initialMessage]);
    setQuery("");
    nextMessageId.current = 2;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      className="ai-assistant-modal"
      aria-labelledby="ai-assistant-title"
    >
      <Modal.Header>
        <Modal.Title id="ai-assistant-title" className="ai-assistant-modal__title">
          <div className="ai-assistant-modal__avatar mb-1"><AiAssistantIcon/></div>
          <span className="ms-2">
             AI-Помощник
            <small>
              {isDeepSeekConfigured ? "AI режим" : "Локальный режим"}
            </small>
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {messages.length > 1 && (
          <div className="ai-assistant-modal__conversation-actions">
            <button type="button" onClick={handleNewConversation} disabled={isAnswering}>
              Новый диалог
            </button>
          </div>
        )}
        <div className="ai-assistant-modal__messages" aria-live="polite">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`ai-assistant-message ai-assistant-message--${message.role}`}
            >
              {message.text && <p>{message.text}</p>}
              {message.article && (
                <>
                  <strong>{message.article.title}</strong>
                  {!message.generatedByAi && (
                    <ol>
                      {message.article.content.map((step) => <li key={step}>{step}</li>)}
                    </ol>
                  )}
                  <Button
                    appearance="primary"
                    size="sm"
                    disabled={!resolveRoute(message.article.route.template)}
                    onClick={() => handleOpenArticle(message.article as AssistantArticle)}
                  >
                    {resolveRoute(message.article.route.template)
                      ? `Открыть: ${message.article.route.label}`
                      : "Сначала выберите станцию"}
                  </Button>
                  {message.suggestions && message.suggestions.length > 0 && (
                    <div className="ai-assistant-message__related">
                      Возможно, также подойдёт: {message.suggestions.map((article) => article.title).join(", ")}.
                    </div>
                  )}
                  <div className="ai-assistant-message__feedback">
                    {message.feedback === undefined ? (
                      <>
                        <span>Ответ помог?</span>
                        <button type="button" onClick={() => handleFeedback(message, true)}>
                          Да
                        </button>
                        <button type="button" onClick={() => handleFeedback(message, false)}>
                          Нет
                        </button>
                      </>
                    ) : (
                      <span>{message.feedback ? "Спасибо за оценку!" : "Спасибо! Улучшим эту инструкцию."}</span>
                    )}
                  </div>
                </>
              )}
              {message.loading && <span className="ai-assistant-message__typing" aria-label="Ожидание ответа" />}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {messages.length === 1 && (
          <div className="ai-assistant-modal__quick-questions">
            {quickQuestions.map((question) => (
              <button key={question} type="button" onClick={() => addAnswer(question)}>
                {question}
              </button>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="ai-assistant-modal__footer">
        <Input
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          disabled={isAnswering}
          placeholder="Например: где настроить вторичные часы?"
          aria-label="Вопрос помощнику"
        />
        <Button
          appearance="primary"
          onClick={() => addAnswer(query)}
          disabled={!query.trim() || isAnswering}
          aria-label="Отправить вопрос"
        >
          <FiSendIcon />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AiAssistantModal;
